import { formatBuild, formatReleaseDay, formatVersion } from '@/shared/format/version';
import { CHANGELOG, CHANGELOG_NOTE } from '@/shared/release/changelog';
import { strings } from '@/shared/strings';
import { useListMotion } from '@/ui/motion/useListMotion';
import { BottomSheet } from '@/ui/primitives/BottomSheet';
import styles from './WhatsNewSheet.module.css';

/**
 * What's new: every version of the app with its day and a short list of what
 * changed, newest first. The request of 5 October 2026 (third), section 8.
 *
 * Opened from the Account card of your own profile by holders of the `admin`
 * permission. Read only: nothing to confirm, nothing to undo, so there is no
 * footer and the sheet's own close, a swipe, the backdrop and Escape are the
 * ways out.
 *
 * The list is the one in the bundle, which always tops out at the version that
 * is loaded, so "This version" is simply its first entry and no comparison is
 * needed. Above it, what this device runs, and under that the build id, which
 * tells two builds of one version apart when support asks.
 */
export function WhatsNewSheet({ onClose }: { onClose: () => void }) {
  const list = useListMotion<HTMLOListElement>(CHANGELOG.map((entry) => entry.version));

  return (
    <BottomSheet tall title={strings.versions.title} closeLabel={strings.sheet.close} onClose={onClose}>
      <div className={styles.body}>
        <p className={styles.device}>
          {strings.versions.thisDevice(formatVersion())}
          <span className={styles.build} data-testid="app-build">
            {strings.versions.build(formatBuild())}
          </span>
        </p>

        <ol ref={list} className={styles.entries}>
          {CHANGELOG.map((entry, index) => (
            <li key={entry.version} data-motion-key={entry.version} className={styles.entry}>
              <div className={styles.head}>
                <h4>{formatVersion(entry.version, '')}</h4>
                {index === 0 && <span className={styles.badge}>{strings.versions.current}</span>}
                <time dateTime={entry.date}>{formatReleaseDay(entry.date)}</time>
              </div>
              <ul className={styles.changes}>
                {entry.changes.map((change) => (
                  <li key={change}>{change}</li>
                ))}
              </ul>
            </li>
          ))}
        </ol>

        <p className={styles.note}>{CHANGELOG_NOTE}</p>
        {/* Whose data the address form is built on, as both licences ask. The
            request of 6 October 2026 (fifth), section 2: "check and report the
            dataset's license". */}
        <p className={styles.credits} data-testid="data-credits">
          {strings.address.credits}
        </p>
      </div>
    </BottomSheet>
  );
}
